import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { Activity, Loader2 } from 'lucide-react';
import { config } from '@/lib/config';

interface Tier {
  id: string;
  name: string;
  priceCents: number;
}
interface EventInfo {
  eventId: string;
  name: string;
  date: string;
  status: string;
  tiers: Tier[];
  convenienceFeeCents: number;
}

const dollars = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export function EventTickets() {
  const { tenantId, eventId } = useParams<{ tenantId: string; eventId: string }>();
  const [ev, setEv] = useState<EventInfo | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [tierId, setTierId] = useState<string>('');
  const [quantity, setQuantity] = useState(1);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${config.paymentsUrl}/ticket/event?tenantId=${tenantId}&eventId=${eventId}`)
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.error ?? 'Event not found');
        setEv(d);
        setTierId(d.tiers?.[0]?.id ?? '');
      })
      .catch((e) => setLoadErr(e instanceof Error ? e.message : 'Could not load event'));
  }, [tenantId, eventId]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${config.paymentsUrl}/ticket/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, eventId, tierId, quantity, email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.error ?? 'Could not start checkout');
      window.location.href = data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setSubmitting(false);
    }
  }

  if (loadErr) {
    return <div className="grid min-h-screen place-items-center text-muted-foreground">{loadErr}</div>;
  }
  if (!ev) {
    return (
      <div className="grid min-h-screen place-items-center text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  const tier = ev.tiers.find((t) => t.id === tierId);
  const total = tier ? (tier.priceCents + ev.convenienceFeeCents) * quantity : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/40">
      <div className="mx-auto max-w-2xl px-6 py-16">
        <div className="mb-8 flex items-center gap-2 text-lg font-semibold tracking-tight">
          <span className="grid h-8 w-8 place-items-center rounded-md bg-primary text-primary-foreground">
            <Activity className="h-5 w-5" />
          </span>
          Get tickets
        </div>
        <h1 className="text-3xl font-bold tracking-tight">{ev.name}</h1>
        {ev.date && <p className="mt-1 text-muted-foreground">{ev.date}</p>}

        <form onSubmit={onSubmit} className="mt-8 space-y-6">
          <div className="space-y-2">
            {ev.tiers.map((t) => (
              <label
                key={t.id}
                className={`flex cursor-pointer items-center justify-between rounded-xl border p-4 ${
                  tierId === t.id ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary/40'
                }`}
              >
                <span className="flex items-center gap-3">
                  <input type="radio" name="tier" checked={tierId === t.id} onChange={() => setTierId(t.id)} />
                  <span className="font-medium">{t.name}</span>
                </span>
                <span className="font-semibold text-primary">{dollars(t.priceCents)}</span>
              </label>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <label className="text-sm font-medium">Quantity</label>
            <input
              type="number"
              min={1}
              max={20}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
              className="w-20 rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            <span className="text-sm text-muted-foreground">+ {dollars(ev.convenienceFeeCents)} service fee / ticket</span>
          </div>

          <div>
            <label htmlFor="email" className="text-sm font-medium">Email</label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full max-w-sm rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>

          {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

          <button
            type="submit"
            disabled={submitting || !tier}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? 'Redirecting…' : `Checkout — ${dollars(total)}`}
          </button>
        </form>
      </div>
    </div>
  );
}
